import { useState, useEffect } from "react";
import { Settings, Shield, BarChart3 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { PrimaryButton, SecondaryButton } from "@/components/StandardButton";
import { triggerHaptic } from "@/utils/haptics";
import { useIsMobile } from "@/hooks/use-mobile";

interface CookieConsentData {
  essential: boolean;
  analytics: boolean;
  timestamp: number;
  version: string;
}

const COOKIE_POLICY_VERSION = "1.0";
const CONSENT_STORAGE_KEY = "sendfeedback_cookie_consent";

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    
    if (!storedConsent) {
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      try {
        const consent: CookieConsentData = JSON.parse(storedConsent);
        if (consent.version !== COOKIE_POLICY_VERSION) {
          setShowBanner(true);
        }
      } catch {
        setShowBanner(true);
      }
    }
  }, []);

  useEffect(() => {
    if (showBanner && isMobile) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [showBanner, isMobile]);

  useEffect(() => {
    if (!showBanner) return;
    const firstButton = document.querySelector('[data-cookie-action]') as HTMLElement;
    firstButton?.focus();
  }, [showBanner]);

  const saveConsent = (analytics: boolean) => {
    const consent: CookieConsentData = {
      essential: true,
      analytics,
      timestamp: Date.now(),
      version: COOKIE_POLICY_VERSION,
    };

    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: consent }));
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    if (isMobile) triggerHaptic('success');
    saveConsent(true);
  };

  const handleEssentialOnly = () => {
    if (isMobile) triggerHaptic('light');
    saveConsent(false);
  };

  if (!showBanner) return null;

  return (
    <Card 
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      className="fixed bottom-4 left-4 right-4 md:bottom-6 md:right-6 md:left-auto md:max-w-md z-50 rounded-[20px] shadow-card border border-border/50 animate-in slide-in-from-bottom-2 fade-in duration-200"
    >
      <div className="p-5 text-center">
        {/* Header */}
        <div className="mb-4">
          <h3 id="cookie-banner-title" className="font-semibold text-base mb-1">
            Cookie Preferences
          </h3>
          <p id="cookie-banner-description" className="text-sm text-muted-foreground">
            We use cookies for authentication and error tracking.
          </p>
        </div>

        {/* Details Section */}
        {showDetails && (
          <div className="mb-4 p-4 bg-muted/30 rounded-xl space-y-4 animate-in fade-in duration-200">
            <div className="flex items-start gap-3">
              <Shield className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-sm">Essential</h4>
                <p className="text-muted-foreground text-xs">Required for login and security. Always active.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <BarChart3 className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-sm">Performance</h4>
                <p className="text-muted-foreground text-xs">Error monitoring to fix bugs. Personal data is masked.</p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <SecondaryButton
              data-cookie-action
              onClick={handleEssentialOnly}
              className="flex-1"
            >
              Essential Only
            </SecondaryButton>
            <PrimaryButton
              data-cookie-action
              onClick={handleAcceptAll}
              className="flex-1"
            >
              Accept All
            </PrimaryButton>
          </div>
          
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground mt-1">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="hover:text-foreground transition-colors inline-flex items-center gap-1"
              aria-expanded={showDetails}
            >
              <Settings className="h-3 w-3" />
              {showDetails ? "Hide" : "Details"}
            </button>
            <span className="text-muted-foreground/50">•</span>
            <a
              href="/cookie-policy"
              className="hover:text-foreground transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cookie Policy
            </a>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Helper function to check if analytics cookies are consented
export const hasAnalyticsCookieConsent = (): boolean => {
  try {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!storedConsent) return false;
    
    const consent: CookieConsentData = JSON.parse(storedConsent);
    return consent.analytics === true && consent.version === COOKIE_POLICY_VERSION;
  } catch {
    return false;
  }
};

// Helper function to reopen cookie settings
export const openCookieSettings = () => {
  localStorage.removeItem(CONSENT_STORAGE_KEY);
  window.location.reload();
};
